import { z } from 'zod';
import { checkStatusSchema } from './check.js';
import { formSelectorsSchema } from './form.js';

/**
 * A qc_run_id looks like `qc_20260819_0214_hta3`: date, UTC time, client slug and a
 * short suffix. It is embedded in the canary's email address, which is what makes
 * correlation exact rather than time-window guesswork.
 *
 * @since 1.0.0
 */
export const QC_RUN_ID_PATTERN = /^qc_\d{8}_\d{4}_[a-z0-9]+$/;

export const qcRunIdSchema = z.string().regex(QC_RUN_ID_PATTERN, 'Malformed qc_run_id');

/**
 * One form the canary should exercise, as handed to the Playwright spec at run time
 * so adding a form never means editing the spec.
 *
 * @since 1.0.0
 */
export const canaryFormConfigSchema = z.object({
    formId: z.number().int(),
    formSlug: z.string(),
    formName: z.string(),
    pageUrl: z.url(),
    selectors: formSelectorsSchema,
    thresholdMs: z.number().int(),
});

export type CanaryFormConfig = z.infer<typeof canaryFormConfigSchema>;

/**
 * What the runner is told for one run: the forms, and every timeout it uses so the budget has one source.
 *
 * @since 1.0.0
 */
export const canaryConfigResponseSchema = z.object({
    clientSlug: z.string(),
    clientName: z.string(),
    forms: z.array(canaryFormConfigSchema),
    pollTimeoutMs: z.number().int().nonnegative(),
    runBudgetMs: z.number().int().nonnegative(),
    pageBudgetMs: z.number().int().nonnegative(),
    navigationTimeoutMs: z.number().int().nonnegative(),
    visibilityTimeoutMs: z.number().int().nonnegative(),
    actionTimeoutMs: z.number().int().nonnegative(),
    successTimeoutMs: z.number().int().nonnegative(),
});

export type CanaryConfigResponse = z.infer<typeof canaryConfigResponseSchema>;

export const startCanaryRunRequestSchema = z.object({
    clientSlug: z.string(),
    formSlug: z.string(),
});

export type StartCanaryRunRequest = z.infer<typeof startCanaryRunRequestSchema>;

/**
 * Everything the canary needs for one submission: the run identity, the address to
 * type into the form, and the signed token that puts the site's QC mode into effect
 * for this single submission.
 *
 * @since 1.0.0
 */
export const startCanaryRunResponseSchema = z.object({
    qcRunId: qcRunIdSchema,
    qcEmail: z.email(),
    qcModeToken: z.string(),
    thresholdMs: z.number().int(),
});

export type StartCanaryRunResponse = z.infer<typeof startCanaryRunResponseSchema>;

export const completeCanaryRunRequestSchema = z.object({
    isSubmitted: z.boolean(),
    hasSuccessMessage: z.boolean(),
    waitedMs: z.number().int().min(0),
    error: z.string().max(500).nullable().default(null),
    blockedReason: z.enum(['captcha', 'selector-missing', 'navigation', 'none']).default('none'),
});

export type CompleteCanaryRunRequest = z.infer<typeof completeCanaryRunRequestSchema>;

/**
 * Polled by the canary while it waits for the notification to show up in Mailgun.
 *
 * @since 1.0.0
 */
export const canaryRunStatusSchema = z.object({
    qcRunId: qcRunIdSchema,
    isMailConfirmed: z.boolean(),
    correlationMs: z.number().int().nullable(),
    status: checkStatusSchema,
});

export type CanaryRunStatus = z.infer<typeof canaryRunStatusSchema>;

export const runCanaryRequestSchema = z.object({
    clientSlug: z.string().optional(),
    formId: z.number().int().optional(),
});

export type RunCanaryRequest = z.infer<typeof runCanaryRequestSchema>;

/**
 * What a canary request queued and what it actually started, for one form or for
 * every client. `isMailboxVerified` and `isChecklyConfigured` explain a run that queued
 * but never began.
 *
 * @since 1.2.0
 */
export const canaryRunResultSchema = z.object({
    queued: z.number().int().nonnegative(),
    started: z.number().int().nonnegative(),
    isMailboxVerified: z.boolean(),
    isChecklyConfigured: z.boolean(),
});

export type CanaryRunResult = z.infer<typeof canaryRunResultSchema>;
